import { NO_COMPARISON, variation } from "./analyticsDeltas";
import { localHourOf } from "./analyticsLabels";
import { timeIn } from "./analyticsPeriod";

// As contas sobre as series do resumo (`summary.series`, spec do redesign R10), por dia, ou por hora num
// periodo de um dia (item 12): as mini tendencias e as variacoes das faixas de numeros. Uma variacao
// compara a soma do periodo com o total do anterior (`previousTotalOf`); a de uma taxa compara a taxa de
// cada periodo, feita dos totais das duas metricas nesse periodo (plano dos separadores, E3).
// Com um anterior que os dados so cobrem em parte (`previousComplete` falso) nao ha comparacao.
// Num periodo que acaba hoje o anterior vem cortado do backend (`series.previous_totals`, item 12), e
// nada aqui soma o anterior das series, salvo o previousTotalOf num resumo sem `previous_totals` (D15).
export const sum = (values) => values.reduce((total, value) => total + value, 0);

// O total do anterior de uma serie: o cortado pelo backend, ou, num resumo de antes do corte, a soma do
// anterior desenhado.
export const previousTotalOf = (series, key) => (series.previous_totals ? series.previous_totals[key] : sum(series.metrics[key].previous));

// Os valores do anterior (ou os seus rotulos) postos ao lado de cada balde do periodo, para o desenho e a
// tabela. Os dias alinham pelo indice. As horas alinham pela hora local que cada rotulo traz, porque o spec
// desenha ontem "nas mesmas horas": cada hora de hoje leva a primeira hora de ontem com o mesmo numero, e
// uma hora sem par fica sem anterior. No dia em que a hora muda os dois lados nao tem as mesmas horas: em
// Marco a 1h de ontem nao tem lugar hoje; em Outubro, com 25 horas ontem, a segunda 1h de ontem nao se
// desenha, e com 25 horas hoje as duas 1h de hoje comparam com a 1h de ontem.
export const alignPrevious = (series, values) =>
  series.buckets.map((bucket, index) => {
    const at = series.granularity === "hour" ? series.previous_buckets.findIndex((previous) => localHourOf(previous) === localHourOf(bucket)) : index;
    return values[at] ?? null;
  });

// Uma serie de partes de outra, balde a balde, em percentagem. Fica sem valor um balde sem denominador, e uma
// hora que ainda nao chegou a um dos lados (null, D8 do item 12): as duas metricas podem ser de grupos com
// cortes diferentes, e um 0% lia-se como nenhuma.
export const percentOf = (parts, wholes) => parts.map((part, index) => (part !== null && wholes[index] ? (100 * part) / wholes[index] : null));

// A variacao de uma contagem: a soma do periodo contra o total do anterior (`previousTotalOf`), sem base
// com o anterior a zero.
export const seriesChange = ({ series, key, kind = "count", better, previousComplete, language }) => {
  if (!previousComplete) return NO_COMPARISON;
  const { current } = series.metrics[key];
  return variation({ kind, value: sum(current), previous: previousTotalOf(series, key), better }, language);
};

// A taxa de um periodo em percentagem, ou null sem denominador (um chat sem convites ou sem cartoes).
const rateOf = (part, whole) => (whole ? (100 * part) / whole : null);

// A variacao de uma taxa feita de duas series: em pontos percentuais, ou (`kind: "number"`) na diferenca
// sem unidade, para uma taxa que nao se le como percentagem (os "por 100 cartoes").
export const rateChange = ({ series, part, whole, kind = "percent", better, digits, previousComplete, language }) => {
  if (!previousComplete) return NO_COMPARISON;
  const value = rateOf(sum(series.metrics[part].current), sum(series.metrics[whole].current));
  const previous = rateOf(previousTotalOf(series, part), previousTotalOf(series, whole));
  return variation({ kind, value, previous, better, digits }, language);
};

// Os nomes do anterior (D12): "Ontem" num periodo de um dia que acaba hoje (com corte), "Dia anterior" noutro
// dia, "Periodo anterior" num periodo mais longo; com o corte a legenda acrescenta a hora de `cut.now`.
export const previousNames = ({ series, cut, timeZone, translate, language }) => {
  const label = translate(
    series.granularity === "hour" ? (cut ? "analyticsYesterday" : "analyticsPreviousDay") : "analyticsPreviousPeriodShort",
  );
  return { short: label, legend: cut ? translate("analyticsUntilTime", { label, time: timeIn(cut.now, timeZone, language) }) : label };
};
